import * as Popover from "@radix-ui/react-popover";
import { useMutation } from "@tanstack/react-query";
import React, { type PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";

import {
	faArrowDownLeftAndArrowUpRightToCenter,
	faArrowUpRightAndArrowDownLeftFromCenter,
	faSparkles,
	faTriangleExclamation,
	faXmark,
} from "@fortawesome/pro-solid-svg-icons";

import type {
	DefaultRephraseResponseStreamJson,
	MetaDataDefaultRephraseResponseStreamJson,
	RephraseQuestionType,
} from "@performyard/service-contracts";

import { RephraseBlock } from "./RephraseBlock";

import { postQueryAi } from "../../../api/services";

import Button from "../../Button";
import IconButton from "../../Button/IconButton";
import Icon from "../../Icon/Icon";
import BaseModal from "../../Modal/BaseModal";
import PageBanner from "../../PageBanner";
import css from "./RephraseModule.module.scss";

const Header = ({
	isRetryDisabled,
	onExpand,
	onDismiss,
	isExpand = false,
}: {
	isRetryDisabled: boolean;
	onExpand: () => void;
	onDismiss: () => void;
	isExpand: boolean;
}) => (
	<div>
		<div className="flex items-center justify-between py-1">
			<div className="flex items-center space-x-2">
				<Icon icon={faSparkles} color="neutral-9" size="small" />
				<span className="font-sans text-paragraph-3 leading-6 font-normal text-neutral-900">PerformYard</span>
			</div>
			<div className="flex items-center space-x-2">
				<IconButton
					onClick={onExpand}
					icon={isExpand ? faArrowDownLeftAndArrowUpRightToCenter : faArrowUpRightAndArrowDownLeftFromCenter}
					iconOnly
					testId="expand-header-button"
					aria-label={isExpand ? "Collapse rephrase panel" : "Expand rephrase panel"}
				/>
				<div className="mx-2 h-6 w-px bg-gray-300" />
				<IconButton
					onClick={onDismiss}
					icon={faXmark}
					testId="dismiss-header-button"
					aria-label="Dismiss rephrase suggestions"
				/>
			</div>
		</div>
		{isRetryDisabled && (
			<div className="py-2">
				<PageBanner
					size="large"
					type={"error"}
					isDismissible={false}
					icon={faTriangleExclamation}
					title=""
					description="You have reached the retry limit of 25. Please choose from list below or start a new session to try again."
				/>
			</div>
		)}
	</div>
);

const Content = ({
	answer,
	rephrasedData,
	onInsert,
	handleRephraseDismiss,
	handleRetry,
	handleDismiss,
	retryDisabled,
	height,
	isExpanded,
}: {
	answer: string[];
	rephrasedData: {
		data: DefaultRephraseResponseStreamJson | string;
		inProgress: boolean;
	}[];
	onInsert: (newAnswer: string[]) => void;
	handleRephraseDismiss: (index: number) => void;
	handleRetry: (retryText: string[]) => void;
	handleDismiss: () => void;
	retryDisabled: boolean;
	height?: string;
	isExpanded: boolean;
}) => {
	const scrollRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = () => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	};

	useEffect(() => {
		scrollToBottom();
	}, [rephrasedData]);

	return (
		<div className="bg-white">
			{/* Current Answer Section */}
			<div className="rounded-md bg-gray-50 p-2">
				<div className="flex items-center gap-2">
					<div className="flex flex-1 items-center gap-2 overflow-hidden">
						<span className="font-semibold whitespace-nowrap text-gray-900">Current:</span>
						<p className="overflow-hidden leading-normal text-ellipsis whitespace-nowrap text-gray-700">
							{answer}
						</p>
					</div>
				</div>
			</div>

			<hr className="mt-3 mb-2 h-px w-100 border-none bg-neutral-2" />

			{/* Rephrased Blocks */}
			<div ref={scrollRef} className="overflow-auto" style={{ maxHeight: height, minHeight: "auto" }}>
				{rephrasedData.map((item, index) => (
					<RephraseBlock
						key={index}
						rephrasedData={item}
						onInsert={onInsert}
						onRetry={handleRetry}
						onDismiss={() => handleRephraseDismiss(index)}
						retryDisabled={retryDisabled}
						rephraseOptionsCount={rephrasedData.length}
						isExpanded={isExpanded}
					/>
				))}
			</div>

			{/* Dismiss Button */}
			<Button onClick={handleDismiss} color="green" className={css.dismissButton}>
				Dismiss
			</Button>
		</div>
	);
};

/**
 * RephraseModule component
 */
export type RephraseModuleProps = {
	answer: string[];
	author: string;
	question: string;
	questionType: RephraseQuestionType;
	subject: string;
	subText: string;
	onInsert: (newAnswer: string[]) => void;
	goalName?: string;
	isOpen?: boolean;
	context?: Record<string, unknown>;
	onOpenChange?: (isOpen: boolean) => void;
};

const MAX_RETRY_COUNT = 25;

export const RephraseModule = (props: PropsWithChildren<RephraseModuleProps>) => {
	const {
		question,
		subText,
		subject,
		author,
		answer,
		onInsert,
		isOpen: propIsOpen,
		onOpenChange,
		questionType,
		goalName = "",
	} = props;

	const [isExpand, setExpand] = useState(false);
	const [rephrasedData, setRephrasedData] = useState<
		{ data: DefaultRephraseResponseStreamJson | string; inProgress: boolean }[]
	>([{ data: { answer } as DefaultRephraseResponseStreamJson, inProgress: true }]);
	const [isOpen, setIsOpen] = useState(propIsOpen || false);
	const [isRetryCount, setRetryCount] = useState(0);
	const isRetryDisabled = isRetryCount >= MAX_RETRY_COUNT;
	const hasMutatedRef = useRef(false);
	// Memoize the initial answer to prevent unnecessary re-renders from array reference changes
	const initialAnswer = useMemo(() => answer, []);

	const handleToggleExpand = () => {
		setExpand((prev) => !prev);
	};

	const handleDismiss = () => {
		setIsOpen(false);
		onOpenChange?.(false);
	};

	const handleInsert = (newAnswer: string[]) => {
		onInsert(newAnswer);
		handleDismiss();
	};

	const handleRephraseDismiss = (index: number) => {
		setRephrasedData((prev) => prev.filter((_, i) => i !== index));
	};

	const handleRetry = (retryText: string[]): void => {
		if (isRetryDisabled) return;
		setRetryCount((prevCount) => prevCount + 1);
		setRephrasedData((prev) => [...prev, { data: "", inProgress: true }]);
		rephraseMutation.mutate({ answer: retryText, recreate: true });
	};

	const rephraseMutation = useMutation({
		mutationFn: async ({ answer, recreate }: { answer: string[]; recreate?: boolean }) => {
			return await postQueryAi<MetaDataDefaultRephraseResponseStreamJson>({
				type: "review-authoring-haiku",
				answer,
				author,
				subject,
				subText,
				question,
				goalName,
				questionType,
				findAdditionalContext: false,
				retry: recreate ? "true" : "false",
				responseStyle: "structured",
			});
		},
		onSuccess: (response) => {
			const newData =
				"message" in response
					? response.message
					: response.data?.answer
						? (response.data as DefaultRephraseResponseStreamJson)
						: "No rephrased answer received.";

			setRephrasedData((prev) => {
				const updated = [...prev];
				updated[updated.length - 1] = { data: newData, inProgress: false };
				return updated;
			});
		},
		onError: () => {
			setRephrasedData((prev) => {
				const updated = [...prev];
				updated[updated.length - 1] = { data: "Error rephrasing response", inProgress: false };
				return updated;
			});
		},
	});

	useEffect(() => {
		// Only call the mutation once on mount to prevent redundant API calls
		// when the answer array reference changes (but values are the same)
		if (hasMutatedRef.current) {
			return;
		}
		hasMutatedRef.current = true;
		rephraseMutation.mutate({ answer: initialAnswer });
	}, [initialAnswer]);

	return (
		<div>
			{!isExpand && (
				<Popover.Root open={isOpen} onOpenChange={handleDismiss}>
					<Popover.Trigger asChild>
						<div />
					</Popover.Trigger>
					<Popover.Content
						className="${css.PopoverContent} z-[9999] w-[416px] rounded-lg bg-white py-4 pr-1 pl-4 shadow-[0_4px_30px_0_rgba(0,0,0,0.15)] transition-all duration-500"
						sideOffset={26}
						side="top"
						align="end"
						sticky="always"
						hideWhenDetached={true}
						data-testid="rephrase-content"
						avoidCollisions={false}
					>
						<div className="max-h-[450px] min-h-full overflow-hidden bg-white pr-4">
							<Header
								onDismiss={handleDismiss}
								onExpand={handleToggleExpand}
								isRetryDisabled={isRetryDisabled}
								isExpand={isExpand}
							/>

							<Content
								answer={answer}
								rephrasedData={rephrasedData}
								onInsert={handleInsert}
								handleRephraseDismiss={handleRephraseDismiss}
								handleRetry={handleRetry}
								handleDismiss={handleDismiss}
								retryDisabled={isRetryDisabled}
								height="225px"
								isExpanded={isExpand}
							/>
						</div>

						<Popover.Arrow className="fill-white" offset={26} height={8} width={16} />
					</Popover.Content>
				</Popover.Root>
			)}

			{isExpand && (
				<BaseModal
					isOpen={isExpand}
					handleClose={handleDismiss}
					size="full"
					hideCloseButton={true}
					wrapperClassName="h-full"
				>
					<div data-testid="expanded-section" className="h-full">
						<Header
							onDismiss={handleDismiss}
							onExpand={handleToggleExpand}
							isRetryDisabled={isRetryDisabled}
							isExpand={isExpand}
						/>
						<Content
							answer={answer}
							rephrasedData={rephrasedData}
							onInsert={handleInsert}
							handleRephraseDismiss={handleRephraseDismiss}
							handleRetry={handleRetry}
							handleDismiss={handleDismiss}
							retryDisabled={isRetryDisabled}
							height="577px"
							isExpanded={isExpand}
						/>
					</div>
				</BaseModal>
			)}
		</div>
	);
};
